import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTimes, faCheck, faTrash, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import AddClientUserModal from './AddClientUserModal';
import ClientSavedCardsSection from '../Wallet/ClientSavedCardsSection';
import ClientDocuments from './ClientDocuments';
import { formatDubai } from '../../../utils/dubaiTime';
import { useZohoOrgId, zohoContactUrl } from '../../../utils/zohoOrg';
import WalletClientDetails from '../Wallet/WalletClientDetails';
import InvoiceDrawer from '../InvoiceDrawer';
import RecordPaymentModal from './RecordPaymentModal';
import BookingsList from '../Properties/PropertyDetailsComponents/BookingsList';
import SectionSkeleton, { SkeletonBody } from '../Bookings/BookingDetails/SectionSkeleton';
import './clientDetails.css';
import '../../SharedView/detailCards.css';
import '../Bookings/BookingTasks/bookingTasks.css'

function ClientDetails() {
    const { clientId } = useParams();
    const zohoOrgId = useZohoOrgId();
    const [client, setClient] = useState(null);

    // Linen deposits across all of this client's properties (ledger).
    const [linenDeposits, setLinenDeposits] = useState([]);
    useEffect(() => {
        let cancelled = false;
        axios.get(`/api/linen-deposits/client/${clientId}`)
            .then((res) => { if (!cancelled) setLinenDeposits(res.data?.deposits || []); })
            .catch(() => { if (!cancelled) setLinenDeposits([]); });
        return () => { cancelled = true; };
    }, [clientId]);

    const linenChargedTotal = linenDeposits.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
    const linenRefundedTotal = linenDeposits.reduce((s, d) => s + (parseFloat(d.refunded_total) || 0), 0);
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [editingUserId, setEditingUserId] = useState(null);
    const [editedUser, setEditedUser] = useState({});

    const [isProcessing, setIsProcessing] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Client Tasks
    const [clientTasks, setClientTasks] = useState([]);
    const [newTaskInstruction, setNewTaskInstruction] = useState('');
    const [newTaskReportRequired, setNewTaskReportRequired] = useState(true);
    const [isTaskSaving, setIsTaskSaving] = useState(false);

    // Task edit state
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [editedTask, setEditedTask] = useState({ instruction: '', is_report_required: true });

    // ✅ Admin Allocation display
    const [adminById, setAdminById] = useState({});
    const [adminsLoading, setAdminsLoading] = useState(false);

    // Zoho billing summary (total pending amount + last unpaid invoice)
    const [zohoSummary, setZohoSummary] = useState(null);
    /* Billing-profile rows expand to show that entity's billing details,
       fetched lazily per contact and cached (owner, 2026-09-02). */
    const [openProfile, setOpenProfile] = useState(null);          // zoho_contact_id
    const [profileAddr, setProfileAddr] = useState({});            // id -> data | 'loading'
    const toggleProfile = (contactId) => {
        const id = String(contactId);
        if (openProfile === id) { setOpenProfile(null); return; }
        setOpenProfile(id);
        if (!profileAddr[id]) {
            setProfileAddr((p) => ({ ...p, [id]: 'loading' }));
            axios.get(`/api/clients/${clientId}/billing-address?contact=${id}`)
                .then((r) => setProfileAddr((p) => ({ ...p, [id]: r.data })))
                .catch(() => setProfileAddr((p) => ({ ...p, [id]: { error: 'Could not load from Zoho.' } })));
        }
    };

    // Entities when the summary carries them (multi-billing), else the one
    // default identity every client has.
    const billingEntities = (zohoSummary?.entities && zohoSummary.entities.length > 0)
        ? zohoSummary.entities
        : (client?.zoho_contact_id
            ? [{ profile_id: null, label: 'Default', is_default: true,
                 zoho_contact_id: client.zoho_contact_id,
                 pending_total: zohoSummary?.pending_total ?? 0,
                 pending_count: zohoSummary?.pending_invoices?.length ?? zohoSummary?.pending_count ?? 0 }]
            : []);
    const [zohoSummaryLoading, setZohoSummaryLoading] = useState(true);
    const [zohoSummaryError, setZohoSummaryError] = useState(null);
    const [statementLoading, setStatementLoading] = useState(false);

    // Payment Received form (owner, 2026-09-15) — managers only. Bumping
    // walletRefreshToken re-reads the wallet panel below once a payment
    // lands, since its excess just became wallet cash.
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [walletRefreshToken, setWalletRefreshToken] = useState(0);

    // Separate wallets per billing entity (PLAN_entity_wallets.md): the
    // client's wallets — one tab each. Almost every client has exactly one.
    const [wallets, setWallets] = useState([]);
    const [walletId, setWalletId] = useState(null);
    const fetchWallets = useCallback(async () => {
        try {
            const res = await axios.get(`/api/wallet/clients/${clientId}/wallets`);
            setWallets(res.data?.wallets || []);
        } catch (e) {
            setWallets([]);
        }
    }, [clientId]);
    useEffect(() => { fetchWallets(); }, [fetchWallets, walletRefreshToken]);

    // Statement builder modal (multi-billing: entity + date range + paid toggle)
    const [statementModalOpen, setStatementModalOpen] = useState(false);
    const [stmtEntity, setStmtEntity] = useState('default'); // 'default' | profile id
    const [stmtRange, setStmtRange] = useState('outstanding'); // 'outstanding' = legacy default (no range param)
    const [stmtFrom, setStmtFrom] = useState('');
    const [stmtTo, setStmtTo] = useState('');
    const [stmtIncludePaid, setStmtIncludePaid] = useState(false);
    const [stmtError, setStmtError] = useState('');

    const navigate = useNavigate();

    const accountManagerId = client?.account_manager_id ?? client?.account_manager ?? null;
    const accountCoordinatorId = client?.account_coordinator_id ?? client?.account_coordinator ?? null;

    const getAdminName = (id) => {
        if (!id) return '—';
        return adminById[String(id)] || `#${String(id)}`;
    };

    useEffect(() => {
        const fetchClient = async () => {
            try {
                const response = await axios.get(`/api/clients/${clientId}`);
                if (response.data) {
                    setClient(response.data);
                } else {
                    throw new Error('No client data received');
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching client:', error);
                setError('Error fetching client data');
                setLoading(false);
            }
        };

        fetchClient();
    }, [clientId]);

    useEffect(() => {
        const fetchAdmins = async () => {
            setAdminsLoading(true);
            try {
                const res = await axios.get('/api/team/role', {
                    params: { role: 'admin' },
                    withCredentials: true,
                });

                const rows = Array.isArray(res.data) ? res.data : [];
                const map = {};

                rows.forEach((u) => {
                    const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() + (u.username ? ` (${u.username})` : '');
                    map[String(u.id)] = label.trim() || `#${u.id}`;
                });

                setAdminById(map);
            } catch (err) {
                console.error('Error fetching admins:', err);
                setAdminById({});
            } finally {
                setAdminsLoading(false);
            }
        };

        fetchAdmins();
    }, []);

    const [propertiesLoading, setPropertiesLoading] = useState(true);
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                setPropertiesLoading(true);
                const response = await axios.get(`/api/properties?clientId=${clientId}`);
                setProperties(response.data);
            } catch (error) {
                console.error('Error fetching properties:', error);
                setError('Error fetching properties data');
            } finally {
                setPropertiesLoading(false);
            }
        };

        fetchProperties();
    }, [clientId]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await axios.get(`/api/clients/${clientId}/users`);
            if (response.data && Array.isArray(response.data.users)) {
                setUsers(response.data.users);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Error fetching users data');
            setUsers([]);
        }
    }, [clientId]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const fetchClientTasks = useCallback(async () => {
        try {
            const response = await axios.get(`/api/tasks/client/${clientId}`);
            if (Array.isArray(response.data)) {
                setClientTasks(response.data);
            } else {
                setClientTasks([]);
            }
        } catch (error) {
            console.error('Error fetching client tasks:', error);
            setClientTasks([]);
        }
    }, [clientId]);

    useEffect(() => {
        fetchClientTasks();
    }, [fetchClientTasks]);

    const fetchZohoSummary = useCallback(async () => {
        setZohoSummaryLoading(true);
        setZohoSummaryError(null);
        try {
            const response = await axios.get(`/api/clients/${clientId}/zoho-summary`);
            setZohoSummary(response.data);
        } catch (error) {
            console.error('Error fetching Zoho summary:', error);
            setZohoSummaryError(
                error.response?.data?.error || 'Unable to load Zoho billing summary.'
            );
            setZohoSummary(null);
        } finally {
            setZohoSummaryLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        fetchZohoSummary();
    }, [fetchZohoSummary]);

    // Invoice drawer opened from the Pending Invoices table (owner, 2026-09-03).
    const [pendingInvoiceDrawerId, setPendingInvoiceDrawerId] = useState(null);
    const userRole = localStorage.getItem('userRole');

    const feedbackTimerRef = useRef(null);

    useEffect(() => {
        if (!feedbackMessage) return;

        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);

        feedbackTimerRef.current = setTimeout(() => {
            setFeedbackMessage('');
        }, 6000);

        return () => {
            if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        };
    }, [feedbackMessage]);

    const filteredProperties = Array.isArray(properties)
        ? properties.filter(property => {
            const addressLine1 = property.address_line_1 || '';
            const unitNumber = property.unit_number || '';
            const propertyIdStr = property.id ? property.id.toString() : '';
            const formattedPropertyId = `#${propertyIdStr.padStart(5, '0')}`;
            return (
                addressLine1.toLowerCase().includes(searchTerm.toLowerCase()) ||
                unitNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                propertyIdStr.includes(searchTerm) ||
                formattedPropertyId.includes(searchTerm)
            );
        })
        : [];

    const formatId = (id) => {
        if (!id) return 'N/A';
        return `#${id.toString().padStart(5, '0')}`;
    };

    // Asia/Dubai render (item 8). The old split('.')[0] stripped the Z off the
    // ISO string, so the UTC wall time was shown as if it were local — wrong
    // on every machine, Dubai included.
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return formatDubai(dateString, 'd MMMM yyyy') || 'N/A';
    };

    const formatMoney = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const [payLinkCopied, setPayLinkCopied] = useState(false);

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setPayLinkCopied(true);
            setTimeout(() => setPayLinkCopied(false), 2500);
        } catch {
            // Blocked outside a secure context — show it so it can still be
            // copied by hand rather than failing silently.
            window.prompt('Copy this payment link:', text);
        }
    };

    /* Derived, never fetched: the token is already on the client record, and
       the link is permanent, so there is nothing to ask the server for. */
    const payLinkUrl = client?.pay_link_token
        ? `${window.location.origin}/pay/account/${client.pay_link_token}`
        : '';

    const openStatementModal = () => {
        setStmtEntity('default');
        setStmtRange('outstanding');
        setStmtFrom('');
        setStmtTo('');
        setStmtIncludePaid(false);
        setStmtError('');
        setStatementModalOpen(true);
    };

    const handleCreateStatement = async () => {
        if (stmtRange === 'custom' && (!stmtFrom || !stmtTo)) {
            setStmtError('Pick both dates for a custom range.');
            return;
        }

        const params = {};
        if (stmtEntity && stmtEntity !== 'default') params.entity_id = stmtEntity;
        if (stmtRange !== 'outstanding') {
            params.range = stmtRange;
            if (stmtRange === 'custom') {
                params.from = stmtFrom;
                params.to = stmtTo;
            }
        }
        // Every range, Outstanding included: there it lists everything since the
        // oldest pending invoice, paid invoices and payments as well.
        params.include_paid = stmtIncludePaid ? 'true' : 'false';

        setStatementLoading(true);
        setStmtError('');
        try {
            const response = await axios.get(`/api/clients/${clientId}/statement`, {
                params,
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            // Give the new tab time to load before releasing the object URL.
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            setStatementModalOpen(false);
        } catch (error) {
            console.error('Error creating statement:', error);
            let msg = 'Failed to generate statement.';
            // Error responses arrive as a Blob because responseType is 'blob'.
            try {
                if (error.response?.data && typeof error.response.data.text === 'function') {
                    const parsed = JSON.parse(await error.response.data.text());
                    msg = parsed.error || msg;
                }
            } catch (_) { /* keep default message */ }
            setStmtError(msg);
        } finally {
            setStatementLoading(false);
        }
    };

    const handleEditClick = () => {
        navigate(`/clients/edit/${clientId}`);
    };

    const handleRowClick = (propertyId) => {
        navigate(`/properties/${propertyId}`);
    };

    const handleEditUserClick = (user) => {
        if (editingUserId === user.id) {
            setEditingUserId(null);
            setEditedUser({});
        } else {
            setEditingUserId(user.id);
            setEditedUser({ ...user });
        }
    };

    const handleSaveClick = async (userId) => {
        if (!window.confirm("Are you sure you want to edit this user?")) return;

        setIsProcessing(true);
        setFeedbackMessage("");

        try {
            await axios.put(`/api/users/clients/${userId}`, editedUser);

            setUsers(users.map(user =>
                user.id === userId ? { ...user, ...editedUser } : user
            ));

            setEditingUserId(null);
            setEditedUser({});
            setFeedbackMessage("User updated successfully.");
        } catch (error) {
            console.error('Error updating user:', error);
            const serverError = error.response?.data?.error;

            if (error.response?.status === 400 && serverError) {
                if (serverError === 'Email is already in use') {
                    setFeedbackMessage('Email is already in use. Please use a different email.');
                } else if (serverError.toLowerCase().includes('phone')) {
                    setFeedbackMessage(serverError);
                } else {
                    setFeedbackMessage(serverError);
                }
            } else {
                setFeedbackMessage('Failed to update user. ' + error.message);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteClick = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;

        setIsProcessing(true);
        setFeedbackMessage("");

        try {
            await axios.delete(`/api/users/clients/${userId}`);
            setUsers(users.filter(user => user.id !== userId));
            setFeedbackMessage("User deleted successfully.");
        } catch (error) {
            console.error('Error deleting user:', error);
            setFeedbackMessage("Failed to delete user.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleAddClientTask = async () => {
        const instruction = (newTaskInstruction || '').trim();
        if (!instruction) {
            setFeedbackMessage("Please enter a task instruction.");
            return;
        }

        setIsTaskSaving(true);
        setIsProcessing(true);
        setFeedbackMessage("");

        try {
            await axios.post(`/api/tasks/client/${clientId}`, {
                instruction,
                is_report_required: !!newTaskReportRequired,
            });

            setNewTaskInstruction('');
            setNewTaskReportRequired(true);

            await fetchClientTasks();
            setFeedbackMessage("Task added successfully.");
        } catch (error) {
            console.error('Error adding client task:', error);
            setFeedbackMessage(error.response?.data?.error || "Failed to add task.");
        } finally {
            setIsTaskSaving(false);
            setIsProcessing(false);
        }
    };

    const startEditTask = (task) => {
        if (editingTaskId === task.id) {
            setEditingTaskId(null);
            setEditedTask({ instruction: '', is_report_required: true });
            return;
        }
        setEditingTaskId(task.id);
        setEditedTask({
            instruction: task.instruction || '',
            is_report_required: !!task.is_report_required,
        });
    };

    const cancelEditTask = () => {
        setEditingTaskId(null);
        setEditedTask({ instruction: '', is_report_required: true });
    };

    const saveEditTask = async (taskId) => {
        const instruction = (editedTask.instruction || '').trim();
        if (!instruction) {
            setFeedbackMessage("Please enter a task instruction.");
            return;
        }

        setIsProcessing(true);
        setFeedbackMessage("");

        try {
            await axios.put(`/api/tasks/client/${clientId}/${taskId}`, {
                instruction,
                is_report_required: !!editedTask.is_report_required,
            });

            await fetchClientTasks();
            setEditingTaskId(null);
            setEditedTask({ instruction: '', is_report_required: true });
            setFeedbackMessage("Task updated successfully.");
        } catch (error) {
            console.error('Error updating client task:', error);
            setFeedbackMessage(error.response?.data?.error || "Failed to update task.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteClientTask = async (task) => {
        if (!task?.id) return;

        const ok = window.confirm("Are you sure you want to delete this task?");
        if (!ok) return;

        setIsProcessing(true);
        setFeedbackMessage("");

        try {
            await axios.delete(`/api/tasks/client/${clientId}/${task.id}`);
            await fetchClientTasks();
            setFeedbackMessage("Task deleted successfully.");
        } catch (error) {
            console.error('Error deleting client task:', error);
            setFeedbackMessage(error.response?.data?.error || "Failed to delete task.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Shaped section skeletons (owner, 2026-09-03): the page loads as ITSELF
    // — a wallet-shaped wallet, a users table that already looks like a table —
    // instead of one spinner and then everything at once.
    if (loading) return (
        <div className="client-details-container">
            <div className="header-section">
                <div className="cd-headerId"><span className="sksec__row" style={{ width: 220, height: 22, display: 'block' }} /></div>
            </div>
            <SectionSkeleton title="Wallet" variant="tiles" tiles={2} rows={2} />
            <SectionSkeleton title="Zoho Billing Summary" variant="tiles" tiles={2} rows={0} />
            <SectionSkeleton title="Users" variant="table" cols={5} rows={3} />
            <SectionSkeleton title="Client Details" variant="grid" cols={3} rows={3} />
            <SectionSkeleton title="Billing Profiles" variant="table" cols={3} rows={2} />
        </div>
    );
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className="client-details-container">
            <div className="header-section">
                <div className="cd-headerId">
                    <h2>{client?.display_name || 'Client'}</h2>
                    <span className="cd-idChip">{formatId(clientId)}</span>
                </div>
                <button onClick={handleEditClick} className="edit-button">Edit Client</button>
            </div>

            {/* Statement builder modal */}
            {statementModalOpen && (
                <div className="zoho-stmt-overlay" onClick={() => !statementLoading && setStatementModalOpen(false)}>
                    <div className="zoho-stmt-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="zoho-stmt-title">Create Statement</h3>
                        <p className="zoho-stmt-subtitle">
                            Generates a Statement of Accounts PDF from Zoho Books.
                        </p>

                        {(zohoSummary?.entities || []).length > 1 && (
                            <div className="zoho-stmt-field">
                                <label className="zoho-stmt-label">Billing entity</label>
                                <select
                                    className="zoho-stmt-input"
                                    value={stmtEntity}
                                    onChange={(e) => setStmtEntity(e.target.value)}
                                    disabled={statementLoading}
                                >
                                    {zohoSummary.entities.map((entity) => (
                                        <option
                                            key={entity.profile_id ?? 'default'}
                                            value={entity.is_default ? 'default' : String(entity.profile_id)}
                                        >
                                            {entity.label}{entity.is_default ? ' (Default)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="zoho-stmt-field">
                            <label className="zoho-stmt-label">Date range</label>
                            <select
                                className="zoho-stmt-input"
                                value={stmtRange}
                                onChange={(e) => { setStmtRange(e.target.value); setStmtError(''); }}
                                disabled={statementLoading}
                            >
                                <option value="outstanding">Outstanding (oldest pending → today)</option>
                                <option value="this_month">This month</option>
                                <option value="last_month">Last month</option>
                                <option value="last_3_months">Last 3 months</option>
                                <option value="this_year">This year</option>
                                <option value="custom">Custom range</option>
                            </select>
                        </div>

                        {stmtRange === 'custom' && (
                            <div className="zoho-stmt-dates">
                                <div className="zoho-stmt-field">
                                    <label className="zoho-stmt-label">From</label>
                                    <input
                                        className="zoho-stmt-input"
                                        type="date"
                                        value={stmtFrom}
                                        onChange={(e) => setStmtFrom(e.target.value)}
                                        disabled={statementLoading}
                                    />
                                </div>
                                <div className="zoho-stmt-field">
                                    <label className="zoho-stmt-label">To</label>
                                    <input
                                        className="zoho-stmt-input"
                                        type="date"
                                        value={stmtTo}
                                        onChange={(e) => setStmtTo(e.target.value)}
                                        disabled={statementLoading}
                                    />
                                </div>
                            </div>
                        )}

                        <label className="zoho-stmt-checkbox">
                            <input
                                type="checkbox"
                                checked={stmtIncludePaid}
                                onChange={(e) => setStmtIncludePaid(e.target.checked)}
                                disabled={statementLoading}
                            />
                            Include paid invoices
                        </label>

                        {stmtError && <div className="zoho-stmt-error">{stmtError}</div>}

                        <div className="zoho-stmt-actions">
                            <button
                                className="zoho-stmt-btn zoho-stmt-btn--ghost"
                                onClick={() => setStatementModalOpen(false)}
                                disabled={statementLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="zoho-stmt-btn zoho-stmt-btn--primary"
                                onClick={handleCreateStatement}
                                disabled={statementLoading}
                            >
                                {statementLoading ? 'Generating…' : 'Generate PDF'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Tasks */}
            <div className="client-tasks-section dp-card">
                <div className="client-tasks-title-wrap dp-cardHead">
                    <div className="client-tasks-title-block">
                        <h3 className="client-tasks-title">Client Tasks</h3>
                        <div className="client-tasks-subtitle">
                            Tasks which should be completed at each booking at each property for this client.
                        </div>
                    </div>
                    <span className="client-tasks-count">{clientTasks.length}</span>
                </div>

                <div className="client-tasks-add">
                    <div className="client-tasks-add-row">
                        <input
                            className="client-tasks-input"
                            type="text"
                            placeholder="Add task instruction (for example set AC 22 degrees..)"
                            value={newTaskInstruction}
                            onChange={(e) => setNewTaskInstruction(e.target.value)}
                            maxLength={180}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddClientTask();
                                }
                            }}
                            disabled={isProcessing}
                        />

                        <label className="client-tasks-checkbox">
                            <input
                                type="checkbox"
                                checked={newTaskReportRequired}
                                onChange={(e) => setNewTaskReportRequired(e.target.checked)}
                                disabled={isProcessing}
                            />
                            Report required
                        </label>

                        <button
                            className="client-tasks-add-btn"
                            onClick={handleAddClientTask}
                            disabled={isTaskSaving || isProcessing}
                            title="Add Task"
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>{isTaskSaving ? 'Adding...' : 'Add'}</span>
                        </button>
                    </div>
                </div>

                {clientTasks.length > 0 ? (
                    <div className="client-tasks-list">
                        {clientTasks.map((task, index) => {
                            const isEditing = editingTaskId === task.id;

                            return (
                                <div className="client-task-card" key={task.id}>
                                    <div className="client-task-row">
                                        <div className="client-task-left">
                                            <span className="client-task-number">{index + 1}.</span>

                                            {isEditing ? (
                                                <input
                                                    className="client-tasks-input"
                                                    type="text"
                                                    value={editedTask.instruction}
                                                    onChange={(e) =>
                                                        setEditedTask((p) => ({ ...p, instruction: e.target.value }))
                                                    }
                                                    maxLength={180}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            saveEditTask(task.id);
                                                        } else if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            cancelEditTask();
                                                        }
                                                    }}
                                                    style={{ height: 36 }}
                                                    disabled={isProcessing}
                                                />
                                            ) : (
                                                <div className="client-task-instruction">{task.instruction}</div>
                                            )}
                                        </div>

                                        <div className="client-task-right">
                                            {isEditing ? (
                                                <>
                                                    <label className="client-tasks-checkbox" style={{ padding: '6px 10px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!editedTask.is_report_required}
                                                            onChange={(e) =>
                                                                setEditedTask((p) => ({
                                                                    ...p,
                                                                    is_report_required: e.target.checked
                                                                }))
                                                            }
                                                            disabled={isProcessing}
                                                        />
                                                        Report required
                                                    </label>

                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        className="user-edit-icon user-save-icon"
                                                        title="Save"
                                                        onClick={() => saveEditTask(task.id)}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon={faTimes}
                                                        className="user-edit-icon user-cancel-icon"
                                                        title="Cancel"
                                                        onClick={cancelEditTask}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    {task.is_report_required ? (
                                                        <span className="client-task-badge client-task-badge-required">Report required</span>
                                                    ) : (
                                                        <span className="client-task-badge client-task-badge-optional">No report</span>
                                                    )}

                                                    <FontAwesomeIcon
                                                        icon={faPen}
                                                        className="user-edit-icon edit-user-icon"
                                                        title="Edit"
                                                        onClick={() => startEditTask(task)}
                                                    />

                                                    <button
                                                        className="client-task-delete-btn"
                                                        onClick={() => handleDeleteClientTask(task)}
                                                        title="Delete task"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="client-tasks-empty">
                        No client tasks yet. Add your first instruction above.
                    </div>
                )}

                {!!feedbackMessage && (
                    <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                        {feedbackMessage}
                    </div>
                )}
            </div>

            <div className="properties-section dp-card">
                <div className='properties-section-header dp-cardHead'>
                    <h3 className='client-details'>Properties</h3>
                    <Link to={`/properties/new?clientId=${clientId}`}>
                        <button className="add-property-button">+ Add New Property</button>
                    </Link>
                </div>
                <div className="search-and-add-container">
                    <div className="search-bar-wrapper">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search by Building Name, Unit Number, or Property ID..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="search-bar"
                        />
                    </div>
                </div>

                {filteredProperties.length > 0 ? (
                    <table className="properties-table">
                        <thead>
                            <tr>
                                <th>Property ID</th>
                                <th>Building Name</th>
                                <th style={{ textAlign: 'center' }}>Unit Number</th>
                                <th style={{ textAlign: 'center' }}>Bedrooms</th>
                                <th style={{ textAlign: 'center' }}>Area</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProperties.map((property) => (
                                <tr
                                    key={property.id}
                                    onClick={() => handleRowClick(property.id)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{formatId(property.id)}</td>
                                    <td>{property.address_line_1}</td>
                                    <td style={{ textAlign: 'center' }}>{property.unit_number}</td>
                                    <td style={{ textAlign: 'center' }}>{property.bedrooms}</td>
                                    <td style={{ textAlign: 'center' }}>{property.area}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : propertiesLoading ? (
                    <SkeletonBody variant="table" cols={4} rows={3} />
                ) : (
                    <p className="no-properties">No properties found for this client.</p>
                )}
            </div>

            {/* Zoho Billing Summary */}
            <div className="zoho-summary-section dp-card">
                <div className="zoho-summary-header dp-cardHead">
                    <h3 className="zoho-summary-title">Zoho Billing Summary</h3>
                    {zohoSummary?.zoho_contact_id && (
                        <div className="zoho-header-actions">
                            {/* PERMANENT, so there is nothing to generate — it
                                exists for every client from the moment they do.
                                Shown short because the full token is 64
                                characters of noise nobody reads; Copy takes the
                                real URL. */}
                            {client?.pay_link_token && (
                                <div className="zoho-paylink">
                                    <span className="zoho-paylink-label">
                                        Payment Link for total pending invoices
                                    </span>
                                    <code className="zoho-paylink-short" title={payLinkUrl}>
                                        /pay/account/{String(client.pay_link_token).slice(0, 8)}…
                                    </code>
                                    <button
                                        type="button"
                                        className="zoho-statement-btn zoho-statement-btn--ghost"
                                        onClick={() => copyToClipboard(payLinkUrl)}
                                        title={payLinkUrl}
                                    >
                                        {payLinkCopied ? 'Copied ✓' : 'Copy'}
                                    </button>
                                </div>
                            )}
                            {userRole === 'manager' && (
                                <button
                                    className="zoho-statement-btn zoho-statement-btn--payment"
                                    onClick={() => setPaymentModalOpen(true)}
                                    disabled={zohoSummaryLoading}
                                    title="Record a bank transfer or cash payment and apply it to the pending invoices"
                                >
                                    Payment Received
                                </button>
                            )}
                            <button
                                className="zoho-statement-btn"
                                onClick={openStatementModal}
                                disabled={statementLoading}
                                title="Generate a Statement of Accounts PDF"
                            >
                                {statementLoading ? 'Generating…' : 'Create Statement'}
                            </button>
                        </div>
                    )}
                </div>

                {paymentModalOpen && (
                    <RecordPaymentModal
                        clientId={clientId}
                        pendingInvoices={zohoSummary?.pending_invoices || []}
                        isMulti={(zohoSummary?.entities || []).length > 1}
                        onClose={() => setPaymentModalOpen(false)}
                        wallets={wallets}
                        onRecorded={() => {
                            fetchZohoSummary();
                            setWalletRefreshToken((n) => n + 1);
                        }}
                    />
                )}

                {zohoSummaryLoading ? (
                    <div className="zoho-summary-loading">Loading Zoho billing summary...</div>
                ) : zohoSummaryError ? (
                    <div className="zoho-summary-error">{zohoSummaryError}</div>
                ) : !zohoSummary?.zoho_contact_id ? (
                    <div className="zoho-summary-empty">
                        This client is not linked to a Zoho contact.
                    </div>
                ) : (
                    <>
                        <div className="zoho-summary-card zoho-summary-card--pending-total">
                            <div className="zoho-summary-card-label">Total Pending Amount</div>
                            <div className="zoho-summary-card-value">
                                {formatMoney(zohoSummary.pending_total)}
                            </div>
                        </div>

                        {(zohoSummary.entities || []).length > 1 && (
                            <div className="zoho-entity-strip">
                                {zohoSummary.entities.map((entity) => (
                                    <div
                                        className="zoho-entity-chip"
                                        key={entity.profile_id ?? 'default'}
                                    >
                                        <span className="zoho-entity-chip-label">
                                            {entity.label}{entity.is_default ? ' (Default)' : ''}
                                        </span>
                                        <span className="zoho-entity-chip-amount">
                                            {formatMoney(entity.pending_total)}
                                        </span>
                                        <span className="zoho-entity-chip-count">
                                            {entity.pending_count} pending
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="zoho-summary-invoices-label">
                            Pending Invoices ({(zohoSummary.pending_invoices || []).length})
                        </div>

                        {(zohoSummary.pending_invoices || []).length > 0 ? (
                            <table className="zoho-summary-table">
                                <thead>
                                    <tr>
                                        <th>Invoice</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        {(zohoSummary.entities || []).length > 1 && <th>Billing entity</th>}
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                        <th style={{ textAlign: 'right' }}>Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zohoSummary.pending_invoices.map((inv) => (
                                        <tr
                                            key={inv.zoho_invoice_id}
                                            className={inv.local_invoice_id ? 'zoho-summary-row--openable' : undefined}
                                            title={inv.local_invoice_id ? 'Open invoice' : undefined}
                                            onClick={inv.local_invoice_id
                                                ? () => setPendingInvoiceDrawerId(inv.local_invoice_id)
                                                : undefined}
                                        >
                                            <td>{inv.id}</td>
                                            <td>{formatDate(inv.date)}</td>
                                            <td>
                                                <span className="zoho-summary-invoice-status">{String(inv.status || '').replace(/_/g, ' ')}</span>
                                            </td>
                                            {(zohoSummary.entities || []).length > 1 && (
                                                <td>{inv.entity_label || '—'}</td>
                                            )}
                                            <td style={{ textAlign: 'right' }}>{formatMoney(inv.amount)}</td>
                                            <td style={{ textAlign: 'right' }} className="zoho-summary-invoice-balance">
                                                {formatMoney(inv.balance)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="zoho-summary-empty">
                                No invoices with a remaining balance.
                            </div>
                        )}
                    </>
                )}
            </div>


            {/* The wallet, right below the Zoho billing summary — the separate
                /wallet page folded in here (owner, 2026-09-02). Same component
                the old page used, so nothing can drift between the two. */}
            <WalletClientDetails
                key={walletId || clientId}
                clientId={String(walletId || clientId)}
                embedded
                onInvoicesChanged={() => { fetchZohoSummary(); fetchWallets(); }}
                refreshToken={walletRefreshToken}
                walletLabel={wallets.length > 1 ? (wallets.find((w) => w.id === (walletId || wallets[0].id))?.label || null) : null}
                // Separate entity wallets: the wallet picker sits in the card's
                // own title row, beside the wallet name (owner 2026-09-23).
                titleExtra={wallets.length > 1 ? (
                    <div className="cd__walletTabs">
                        {wallets.map((w) => (
                            <button
                                type="button"
                                key={w.id}
                                className={`cd__walletTab${(walletId || wallets[0].id) === w.id ? ' is-active' : ''}`}
                                onClick={() => setWalletId(w.id)}
                            >
                                <span className="cd__walletTabLabel">{w.label}</span>
                                <span className="cd__walletTabAmount">{formatMoney(w.cash_balance + w.bonus_balance)}</span>
                            </button>
                        ))}
                    </div>
                ) : null}
            />

            <InvoiceDrawer
                open={pendingInvoiceDrawerId != null}
                invoiceId={pendingInvoiceDrawerId}
                canEdit={userRole === 'manager'}
                onClose={() => setPendingInvoiceDrawerId(null)}
                onChanged={fetchZohoSummary}
            />

            <div className="users-section dp-card">
                <div className="users-section-header dp-cardHead">
                    <h3>Users</h3>
                    <button className="add-new-client-user" onClick={() => setIsModalOpen(true)}>+ Add New User</button>
                </div>

                {users.length > 0 ? (
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>User ID</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id}>
                                    <td>{formatId(user.id)}</td>

                                    <td>
                                        {editingUserId === user.id ? (
                                            <input
                                                type="text"
                                                value={editedUser.first_name || ''}
                                                onChange={(e) => setEditedUser({ ...editedUser, first_name: e.target.value })}
                                            />
                                        ) : (
                                            user.first_name
                                        )}
                                    </td>

                                    <td>
                                        {editingUserId === user.id ? (
                                            <input
                                                type="text"
                                                value={editedUser.last_name || ''}
                                                onChange={(e) => setEditedUser({ ...editedUser, last_name: e.target.value })}
                                            />
                                        ) : (
                                            user.last_name
                                        )}
                                    </td>

                                    <td>
                                        {editingUserId === user.id ? (
                                            <input
                                                type="text"
                                                value={editedUser.email || ''}
                                                onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                                            />
                                        ) : (
                                            user.email
                                        )}
                                    </td>

                                    <td>
                                        {editingUserId === user.id ? (
                                            <PhoneInput
                                                defaultCountry="ae"
                                                value={
                                                    editedUser.phone_number !== undefined
                                                        ? editedUser.phone_number
                                                        : user.phone_number || ''
                                                }
                                                onChange={(value) =>
                                                    setEditedUser((prev) => ({
                                                        ...prev,
                                                        phone_number: value,
                                                    }))
                                                }
                                                placeholder="Enter phone number"
                                                className="client-user-phone-wrapper"
                                                inputClassName="client-user-phone-input"
                                            />
                                        ) : (
                                            <>
                                                {user.phone_number}
                                                {user.phone_number && user.is_valid_wa_number === false && (
                                                    <span
                                                        className="wa-invalid-badge"
                                                        title="3 WhatsApp messages to this number failed as invalid — WhatsApp is off for this user. Editing the number turns it back on automatically."
                                                    >
                                                        NOT VALID WA NUMBER
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </td>

                                    <td style={{ textAlign: 'right' }}>
                                        {editingUserId === user.id ? (
                                            <>
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="user-edit-icon user-delete-icon"
                                                    onClick={() => handleDeleteClick(user.id)}
                                                    title="Delete"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="user-edit-icon user-save-icon"
                                                    onClick={() => handleSaveClick(user.id)}
                                                    title="Save"
                                                />
                                                <FontAwesomeIcon
                                                    icon={faTimes}
                                                    className="user-edit-icon user-cancel-icon"
                                                    onClick={() => handleEditUserClick(user)}
                                                    title="Cancel"
                                                />
                                            </>
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faPen}
                                                className="user-edit-icon edit-user-icon"
                                                onClick={() => handleEditUserClick(user)}
                                                title="Edit"
                                            />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-users">No users found for this client.</p>
                )}

                {!!feedbackMessage && (
                    <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                        {feedbackMessage}
                    </div>
                )}
            </div>

            <div className="zoho-summary-section cd-detailsCard dp-card">
                <div className="cd-detailsHead">
                    <h3 className="zoho-summary-title">Client Details</h3>
                    <button onClick={handleEditClick} className="edit-button">Edit Client</button>
                </div>

            {client ? (
                <div className="client-info">
                    <div className="client-info-row">
                        <span className="client-info-item">
                            <strong>ID:</strong> {formatId(client.id)}
                        </span>
                        <span className="client-info-item">
                            <strong>Primary Contact Name:</strong> {client.first_name} {client.last_name}
                        </span>
                        <span className="client-info-item">
                            <strong>Client Display Name:</strong> {client.display_name || '—'}
                        </span>
                    </div>

                    <div className="client-info-row">
                        <span className="client-info-item">
                            <strong>Registration Date:</strong> {formatDate(client.registration)}
                        </span>
                        <span className="client-info-item">
                            <strong>Company Name:</strong> {client.company_name || '—'}
                        </span>
                        <span className="client-info-item">
                            <strong>Total Deposit Collected:</strong>{' '}
                            {linenDeposits.length === 0
                                ? '—'
                                : <>AED {linenChargedTotal.toFixed(2)}{linenRefundedTotal > 0 ? ` (AED ${linenRefundedTotal.toFixed(2)} refunded)` : ''}</>}
                        </span>
                    </div>

                    <div className="client-info-row">
                        <span className="client-info-item">
                            <strong>Primary Contact Number:</strong> {client.contact_number || '—'}
                        </span>
                        <span className="client-info-item">
                            <strong>Whats App Number or Group:</strong> {client.primary_communication_method || '—'}
                        </span>
                        <span className="client-info-item">
                            <strong>Email:</strong> {client.email || '—'}
                        </span>
                    </div>

                    <div className="client-info-row">
                        <span className="client-info-item">
                            <strong>Account Manager:</strong> {adminsLoading ? 'Loading...' : getAdminName(accountManagerId)}
                        </span>
                        <span className="client-info-item">
                            <strong>Account Coordinator:</strong> {adminsLoading ? 'Loading...' : getAdminName(accountCoordinatorId)}
                        </span>
                        <span className="client-info-item">
                            <strong>Zoho Books:</strong>{' '}
                            {client.zoho_contact_id ? (
                                zohoOrgId ? (
                                    <a
                                        href={zohoContactUrl(zohoOrgId, client.zoho_contact_id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {client.zoho_contact_id}
                                    </a>
                                ) : (
                                    <span>{client.zoho_contact_id}</span>
                                )
                            ) : (
                                '—'
                            )}
                        </span>
                    </div>

                    {/* Client documents — view + upload only; replace/delete via Edit Client */}
                    <ClientDocuments clientId={clientId} manage={false} />
                </div>
            ) : (
                <p className="error-message">Client data is unavailable.</p>
            )}
            </div>

            {/* Billing Profiles — read-only overview; editing lives in Edit
                Client. Shown for EVERY client now, not only multi-billing:
                a default-only client still has a billing identity, and "which
                Zoho contact is this?" was unanswerable from this page
                (owner, 2026-09-02). */}
            {(billingEntities.length > 0) && (
                <div className="zoho-profiles-section dp-card">
                    <h3 className="zoho-profiles-title">Billing Profiles</h3>
                    <div className="zoho-profiles-list">
                        {billingEntities.map((entity) => {
                            const cid = String(entity.zoho_contact_id);
                            const isOpen = openProfile === cid;
                            const addr = profileAddr[cid];
                            return (
                                <div className={`zoho-profiles-item ${isOpen ? 'is-open' : ''}`} key={entity.profile_id ?? 'default'}>
                                    <div
                                        className="zoho-profiles-row"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleProfile(cid)}
                                        onKeyDown={(e) => (e.key === 'Enter') && toggleProfile(cid)}
                                    >
                                        <span className={`zp-chevron ${isOpen ? 'is-open' : ''}`}>›</span>
                                        <div className="zoho-profiles-name">
                                            {entity.label}
                                            {entity.is_default && <span className="zoho-profiles-badge">Default</span>}
                                        </div>
                                        <div className="zoho-profiles-meta">
                                            {zohoOrgId ? (
                                                <a
                                                    href={zohoContactUrl(zohoOrgId, entity.zoho_contact_id)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {entity.zoho_contact_id}
                                                </a>
                                            ) : (
                                                <span>{entity.zoho_contact_id}</span>
                                            )}
                                        </div>
                                        <div className="zoho-profiles-pending">
                                            {formatMoney(entity.pending_total)} pending &middot; {entity.pending_count} invoice{entity.pending_count === 1 ? '' : 's'}
                                        </div>
                                    </div>
                                    {isOpen && (
                                        <div className="zp-detail">
                                            {addr === 'loading' || !addr ? (
                                                <span className="zp-muted">Loading billing details…</span>
                                            ) : addr.error ? (
                                                <span className="zp-muted">{addr.error}</span>
                                            ) : (
                                                <>
                                                    <span><strong>Address:</strong> {[addr.address, addr.street2].filter(Boolean).join(', ') || '—'}</span>
                                                    <span><strong>City / Emirate:</strong> {[addr.city, addr.state].filter(Boolean).join(', ') || '—'}</span>
                                                    <span><strong>Country:</strong> {addr.country || '—'}</span>
                                                    <span><strong>TRN:</strong> {addr.trn || '—'}</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* The client's saved cards belong with their billing
                        identities, not inside the wallet (owner, 2026-09-02). */}
                    <ClientSavedCardsSection clientId={clientId} />
                </div>
            )}

            {/* BOOKINGS CLOSE THE PAGE (owner, 2026-09-18): upcoming and past,
                the property page's own card, across every property of the
                client. */}
            <BookingsList clientId={clientId} />

            {isModalOpen && (
                <AddClientUserModal
                    clientId={clientId}
                    onClose={() => setIsModalOpen(false)}
                    onUserAdded={fetchUsers}
                />
            )}
        </div>
    );
}

export default ClientDetails;